'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { supabase } from '@/lib/supabase/client'

// Routes where the feedback widget should be hidden (onboarding, auth, verification flows).
// /messages: on mobile the floating button covered the thread's Send button.
const HIDDEN_PREFIXES = ['/auth', '/onboarding', '/aupair/verify', '/family/verify', '/messages']

export default function FeedbackWidget() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [rating, setRating] = useState(0)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Don't render during auth/onboarding/verification flows — distracting and premature
  if (HIDDEN_PREFIXES.some(prefix => pathname?.startsWith(prefix))) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (rating === 0) return

    setSubmitting(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()

      await supabase.from('feedback').insert({
        user_id: user?.id || null,
        rating,
        message: message.trim() || null,
        page_url: typeof window !== 'undefined' ? window.location.pathname : '/',
      })

      setSubmitted(true)
      setTimeout(() => {
        setOpen(false)
        setSubmitted(false)
        setRating(0)
        setMessage('')
      }, 2000)
    } catch (error) {
      console.error('Feedback error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-all hover:scale-105 flex items-center justify-center"
        aria-label="Give feedback"
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Feedback panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-80 bg-white rounded-xl shadow-2xl border overflow-hidden">
          {submitted ? (
            <div className="p-6 text-center">
              <p className="text-2xl mb-2">Thank you!</p>
              <p className="text-gray-600">Your feedback has been submitted.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="px-5 pt-5 pb-3">
                <h3 className="font-bold text-gray-900 text-lg">Send Feedback</h3>
                <p className="text-sm text-gray-500 mt-1">How&apos;s your experience?</p>
              </div>

              {/* Star rating */}
              <div className="px-5 pb-3 flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className={`text-xl sm:text-2xl leading-none transition-colors ${
                      star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                    }`}
                  >
                    &#9733;
                  </button>
                ))}
              </div>

              {/* Message */}
              <div className="px-5 pb-3">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us more (optional)..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Submit */}
              <div className="px-5 pb-5">
                <button
                  type="submit"
                  disabled={rating === 0 || submitting}
                  className="w-full py-2 bg-primary-600 text-white rounded-lg font-semibold text-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Sending...' : 'Submit Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  )
}
